import axios, { AxiosRequestConfig, AxiosResponse } from 'axios'
import CONSTANTS from '../Constants'
import { PAGES_ROUTES } from '../Constants/PagesRoutes'
import { getLocalStorage } from '../Helpers/Storage'
import { getParsedToken, logoutCyberArk, setRefreshTokenCallback } from '../Helpers/Util'
import { store } from '../Store'
import { showAlert } from '../Store/Slice/alert.slice'

axios.interceptors.request.use(
  (config) => {
    const initialHeader = config.headers['Content-Type']
    const configCopy = { ...config }
    configCopy.headers['Content-Type'] = initialHeader || 'application/json'
    const parsedToken: any = getParsedToken()
    if (parsedToken?.sub?.sessionId) {
      configCopy.headers['device_id'] = `${parsedToken.sub.sessionId}${
        parsedToken?.exp ? '-' + parsedToken.exp : ''
      }`
      configCopy.headers['session_id'] =
        `${parsedToken.sub.sessionId}-${window?.navigator?.userAgent}`
    }
    if (!configCopy.headers['Authorization'] && !config.headers['upload']) {
      const { token = '' } = getLocalStorage('auth_config') as any
      if (token) configCopy.headers['Authorization'] = ` bearer ${token}`
    }
    return configCopy
  },
  (error) => Promise.reject(error),
)

axios.interceptors.response.use(
  function (response) {
    setRefreshTokenCallback()
    return response?.data
  },
  function (error) {
    const status = error?.response?.status
    const errorData = error?.response?.data
    const responseCode = errorData?.responseCode || errorData?.code

    // Check for maintenance error codes
    const isMaintenanceError =
      status === 503 || // Service Unavailable
      responseCode === 503 ||
      (typeof responseCode === 'number' && responseCode >= 503 && responseCode < 600)

    if (isMaintenanceError) {
      window.location.href = PAGES_ROUTES.UnderMaintenance
      return Promise.reject(error)
    }

    switch (status) {
      case 401:
        logoutCyberArk(error?.response?.data?.message)
        break
      case 403:
        store.dispatch(showAlert([true, 'Access Forbidden', 'error']))
        break
      case 500:
        store.dispatch(showAlert([true, 'Internal Server Error', 'error']))
        break
    }
    if (error?.response?.data) throw error.response.data
    throw error
  },
)

const mainApiService = async ({
  baseUrl,
  url,
  method,
  data,
  params,
  headers,
  withoutBaseURL = false,
  signal,
}: {
  baseUrl?: string | undefined
  url: string
  method: AxiosRequestConfig['method']
  data?: AxiosRequestConfig['data']
  params?: AxiosRequestConfig['params']
  headers?: AxiosRequestConfig['headers']
  withoutBaseURL?: boolean
  signal?: any
}): Promise<{
  data?: AxiosRequestConfig['data']
  error?: any
  responseCode?: any
  message?: any
  errorMessage?: string[]
  boltCode?: string
}> => {
  try {
    const bUrl = baseUrl ?? CONSTANTS.BASE_URL
    let apiURL: string = bUrl + url
    if (withoutBaseURL) {
      apiURL = url
    }

    const result = await axios({
      url: apiURL,
      method,
      data,
      params,
      headers,
      ...(signal ? { signal } : {}),
    })
    return result as AxiosResponse
  } catch (axiosError) {
    const err = axiosError as any
    const errorMessage = err?.response?.data?.errors || []
    return { error: err, errorMessage }
  }
}

export default mainApiService
